/**
 * `match` — 1v1 filtr. docs/SPEC.md §match, faza 1.
 *
 * Prototipdan IKKI CHETLANISH (docs/SPEC.md §1):
 *
 *   X-5  "Har kuni 1 ta qidiruv bepul · yarim kechada yangilanadi"
 *        qatori YO'Q. D-016 tekin qidiruvni butunlay bekor qilgan.
 *   X-6  "Farqi yo'q" ham PULLIK. Prototipda `Bepul` deb turgan edi.
 *
 * Narxlar bu faylda yo'q — `GET /config` dan (qoida 22, D-017).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useBackButton } from '../app/useBackButton';
import { uz } from '../i18n/uz';
import { useStartSearch } from '../lib/api/useMatch';
import { useMe } from '../lib/api/useMe';
import { useConfig } from '../lib/config/useConfig';
import { fmt } from '../lib/format';
import { CTAButton, Chip, Icon, Toast, Toggle } from '../ui';
import { useToast } from '../ui/useToast';
import './screens.css';

type Gender = 'male' | 'female' | null;
type AgeRange = '16-22' | '23-27' | '28-35' | '35+';
type Lang = 'uz' | 'ru' | 'en' | null;

const AGE_RANGES: AgeRange[] = ['16-22', '23-27', '28-35', '35+'];

export function Match() {
  const navigate = useNavigate();
  const config = useConfig();
  const me = useMe();
  const startSearch = useStartSearch();
  const toast = useToast();

  const [gender, setGender] = useState<Gender>(null);
  const [ageRange, setAgeRange] = useState<AgeRange>('23-27');
  const [lang, setLang] = useState<Lang>(null);
  const [anonymous, setAnonymous] = useState(false);

  useBackButton(true, () => navigate('/'));

  const prices = config.data?.prices;
  // Narx filtrga bog'liq: jins tanlansa qimmatroq (D-060).
  const price = gender
    ? Number(prices?.['match_search_default'] ?? 0)
    : Number(prices?.['match_search_any'] ?? 0);
  const revealMinutes = Math.round(Number(config.data?.limits['anon_reveal_seconds'] ?? 180) / 60);

  async function start(): Promise<void> {
    if (me.data && me.data.gems < price) {
      // SPEC: balans yetmasa CTA `shop` ga olib boradi.
      toast.show(uz.match.notEnough);
      navigate('/shop');
      return;
    }

    try {
      const started = await startSearch.mutateAsync({
        gender,
        age_range: ageRange,
        lang,
        anonymous,
        client_tx_id: crypto.randomUUID(),
      });
      // ⚠️ **Juftlik SO'ROV PAYTIDA topilgan bo'lishi mumkin.**
      //
      // Server juftlikni `POST /match/search` ichida yopadi va
      // `match.found` ni DARHOL chiqaradi. Event HTTP javobidan
      // OLDIN kelishi mumkin: o'shanda `MatchFoundRedirect` (App.tsx)
      // bizni `/call` ga olib o'tadi va quyidagi `navigate('/search')`
      // uni USTIDAN YOZARDI — foydalanuvchi radar ekranida qolib,
      // suhbatdoshi esa yolg'iz kutardi.
      //
      // Shuning uchun: hali shu ekranda ekanimizni tekshiramiz.
      if (window.location.pathname !== '/match') return;

      // Sonni JAVOBDAN uzatamiz: `match.queued` eventi POST paytida
      // chiqadi va radar ekrani hali ochilmagan bo'ladi — ya'ni uni
      // hech kim eshitmaydi.
      navigate('/search', {
        state: {
          othersWaiting: started.others_waiting,
          timeoutSeconds: started.timeout_seconds,
        },
      });
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'ALREADY_IN_QUEUE') {
        // Qidiruv allaqachon ketmoqda (ilova qayta ochilgan bo'lishi
        // mumkin). Xato ko'rsatish o'rniga radarga qaytaramiz — pul
        // yechilgan, qidiruv esa haqiqatan davom etyapti.
        navigate('/search');
        return;
      }
      if (code === 'PROFILE_INCOMPLETE') {
        // Faqat toast YETARLI EMAS: u nima qilish kerakligini aytadi-yu,
        // qayerda qilishni aytmaydi. Tahrirlash ekrani Sozlamalar
        // ichida ko'milgan — odam uni topmasdi va 1v1 shu yerda
        // to'xtardi. `INSUFFICIENT_FUNDS` da ham xuddi shu naqsh:
        // xatoni to'g'ridan-to'g'ri hal qiladigan joyga olib boramiz.
        toast.show(uz.match.profileIncomplete);
        navigate('/profile/edit');
        return;
      }
      if (code === 'INSUFFICIENT_FUNDS') {
        toast.show(uz.match.notEnough);
        navigate('/shop');
        return;
      }
      toast.show(err instanceof Error ? err.message : uz.common.error);
    }
  }

  if (config.isLoading) {
    return (
      <div className="screen">
        <div className="empty">{uz.common.loading}</div>
      </div>
    );
  }

  // Config kelmasa NARXLARNI ko'rsatib bo'lmaydi (qoida 22 — ular faqat
  // serverdan). Ilgari bu shox yo'q edi va ekran `0 💎` chizardi:
  // foydalanuvchi qidiruv TEKIN deb o'ylardi, keyin esa pul yechilardi.
  // Noto'g'ri narx ko'rsatishdan ko'ra ekranni ko'rsatmaslik to'g'ri.
  if (config.isError || !config.data) {
    return (
      <div className="screen">
        <div className="empty">{uz.common.networkError}</div>
        <CTAButton variant="ghost" onClick={() => void config.refetch()}>
          {uz.common.retry}
        </CTAButton>
      </div>
    );
  }

  return (
    <div className="screen">
      {toast.message && <Toast message={toast.message} />}

      <div className="match__label">{uz.match.label}</div>
      <h1 className="match__title">{uz.match.title}</h1>
      <p className="match__hint">{uz.match.hint}</p>

      <div className="field-label">{uz.match.gender}</div>
      <div className="gender-grid">
        <GenderCard
          symbol="♂"
          mod="male"
          name={uz.match.male}
          price={Number(prices?.['match_search_default'] ?? 0)}
          active={gender === 'male'}
          onPick={() => setGender('male')}
        />
        <GenderCard
          symbol="♀"
          mod="female"
          name={uz.match.female}
          price={Number(prices?.['match_search_default'] ?? 0)}
          active={gender === 'female'}
          onPick={() => setGender('female')}
        />
        {/* X-6 — bu ham PULLIK. Prototipda `Bepul` edi. */}
        <GenderCard
          symbol="∞"
          mod="any"
          name={uz.match.anyGender}
          price={Number(prices?.['match_search_any'] ?? 0)}
          active={gender === null}
          onPick={() => setGender(null)}
        />
      </div>

      <div className="field-label">{uz.match.age}</div>
      <div className="chip-row">
        {AGE_RANGES.map((range) => (
          <Chip
            key={range}
            active={ageRange === range}
            onClick={() => setAgeRange(range)}
            className="chip--age tnum"
          >
            {range}
          </Chip>
        ))}
      </div>

      <div className="field-label">{uz.match.lang}</div>
      <div className="chip-row chip-row--wrap">
        <Chip className="chip--lang" active={lang === 'uz'} onClick={() => setLang('uz')}>
          {uz.match.langUz}
        </Chip>
        <Chip className="chip--lang" active={lang === 'ru'} onClick={() => setLang('ru')}>
          {uz.match.langRu}
        </Chip>
        <Chip className="chip--lang" active={lang === 'en'} onClick={() => setLang('en')}>
          {uz.match.langEn}
        </Chip>
        <Chip className="chip--lang" active={lang === null} onClick={() => setLang(null)}>
          {uz.match.anyLang}
        </Chip>
      </div>

      <div className="anon-card">
        <Icon name="eyeoff" size={18} color="var(--lavender)" />
        <div className="anon-card__body">
          <div className="anon-card__title">{uz.match.anonTitle}</div>
          <div className="anon-card__hint">{uz.match.anonHint(String(revealMinutes))}</div>
        </div>
        <Toggle checked={anonymous} onChange={setAnonymous} label={uz.match.anonTitle} />
      </div>

      <div style={{ marginTop: 24 }}>
        <CTAButton
          variant="match"
          disabled={startSearch.isPending || config.isLoading}
          price={`${fmt(price)} 💎`}
          onClick={() => void start()}
        >
          {uz.match.start}
        </CTAButton>
      </div>

      <div className="screen-tail screen-tail--flat" />
    </div>
  );
}

function GenderCard({
  symbol,
  mod,
  name,
  price,
  active,
  onPick,
}: {
  symbol: string;
  mod: 'male' | 'female' | 'any';
  name: string;
  price: number;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <button type="button" className="gender-card" aria-pressed={active} onClick={onPick}>
      <div className={`gender-card__symbol gender-card__symbol--${mod}`}>{symbol}</div>
      <div className="gender-card__name">{name}</div>
      <div className="gender-card__price tnum">{`${fmt(price)} 💎`}</div>
    </button>
  );
}
